import React, { createContext, useContext, useState, useEffect, useCallback, ReactNode } from 'react';
import axios from 'axios';
import { useNavigate } from 'react-router-dom';

axios.defaults.baseURL = process.env.REACT_APP_API_URL ?? 'http://localhost:3000/api';

/**
 * User interface
 */
export interface User {
  id: string;
  email: string;
  firstName: string;
  lastName: string;
  role: {
    id: string;
    name: string;
    code: string;
    permissions?: {
      permission: {
        code: string;
      };
    }[];
  };
  school?: {
    id: string;
    name: string;
    code: string;
    enabledModules: string[];
  };
  permissions?: string[];
  userPermissions?: {
    permission: {
      code: string;
    };
  }[];
}

interface AuthContextType {
  user: User | null;
  token: string | null;
  login: (email: string, password: string, schoolUsername: string) => Promise<void>;
  logout: () => void;
  refreshUser: () => Promise<User>;
  hasPermission: (permissionCode: string) => boolean;
  isLoading: boolean;
  isAuthenticated: boolean;
}

const AuthContext = createContext<AuthContextType | undefined>(undefined);

/**
 * Auth Provider
 * Manages authentication state and tokens
 */
export const AuthProvider: React.FC<{ children: ReactNode }> = ({ children }) => {
  const [user, setUser] = useState<User | null>(null);
  const [token, setToken] = useState<string | null>(null);
  const [isLoading, setIsLoading] = useState(true);
  const navigate = useNavigate();

  /**
   * Refresh user data from backend (single source of truth)
   * This ensures enabledModules and other user data is always current
   * MUST be called after every module toggle to update UI state
   */
  const refreshUser = useCallback(async () => {
    try {
      // Step 1: Fetch user profile from backend
      const response = await axios.get('/auth/profile', {
        headers: { 'Cache-Control': 'no-cache' },
      });
      const userData = response.data;

      // Step 2: Fetch enabled modules directly from backend (authoritative source)
      let enabledModules: string[] = [];
      if (userData.schoolId) {
        const modulesResponse = await axios.get(`/modules/school/${userData.schoolId}`, {
          headers: { 'Cache-Control': 'no-cache' },
        });
        // Extract enabled module codes from backend response
        const modules = Array.isArray(modulesResponse.data)
          ? modulesResponse.data
          : modulesResponse.data.data || [];

        enabledModules = modules
          .filter((sm: any) => sm.enabled === true)
          .map((sm: any) => sm.module.code);
      }

      // Step 3: Build updated user object with fresh enabledModules
      const updatedUser: User = {
        id: userData.id,
        email: userData.email,
        firstName: userData.firstName,
        lastName: userData.lastName,
        role: {
          id: userData.role.id,
          name: userData.role.name,
          code: userData.role.code,
        },
        school: userData.school
          ? {
            id: userData.school.id,
            name: userData.school.name,
            code: userData.school.code,
            enabledModules, // Fresh from backend
          }
          : undefined,
        permissions: userData.permissions || [], // ✅ Load effective permissions
      };

      // Step 4: Replace entire user state (triggers re-render)
      setUser(updatedUser);
      localStorage.setItem('user', JSON.stringify(updatedUser));

      return updatedUser;
    } catch (error) {
      console.error('Failed to refresh user data:', error);
      throw error;
    }
  }, []);

  const logout = useCallback(() => {
    setToken(null);
    setUser(null);
    localStorage.removeItem('accessToken');
    localStorage.removeItem('refreshToken');
    localStorage.removeItem('user');
    sessionStorage.removeItem('scholar_register_dashboard_filters');
    delete axios.defaults.headers.common['Authorization'];
    navigate('/login');
  }, [navigate]);

  useEffect(() => {
    const storedToken = localStorage.getItem('accessToken');
    const storedUser = localStorage.getItem('user');

    if (storedToken) {
      setToken(storedToken);
      axios.defaults.headers.common['Authorization'] = `Bearer ${storedToken}`;
      refreshUser()
        .catch(() => {
          if (storedUser) {
            setUser(JSON.parse(storedUser));
          }
        })
        .finally(() => {
          setIsLoading(false);
        });
    } else {
      if (storedUser) {
        setUser(JSON.parse(storedUser));
      }
      setIsLoading(false);
    }
  }, [refreshUser]);

  // Configure axios defaults
  useEffect(() => {
    const requestInterceptor = axios.interceptors.request.use(
      (config) => {
        const token = localStorage.getItem('accessToken');
        if (token) {
          config.headers.Authorization = `Bearer ${token}`;
        }
        return config;
      },
      (error) => Promise.reject(error)
    );

    const responseInterceptor = axios.interceptors.response.use(
      (response) => response,
      (error) => {
        if (process.env.NODE_ENV === 'development') {
          // eslint-disable-next-line no-console
          console.error("API ERROR:", error.response?.status, error.response?.data);
        }
        return Promise.reject(error);
      }
    );
    return () => {
      axios.interceptors.request.eject(requestInterceptor);
      axios.interceptors.response.eject(responseInterceptor);
    };
  }, [navigate, logout]);

  const login = async (
    email: string,
    password: string,
    schoolUsername: string,
  ) => {
    try {
      const response = await axios.post('/auth/login', {
        email,
        password,
        schoolUsername,
      });
      const { accessToken, user: userData } = response.data;

      setToken(accessToken);
      setUser(userData);
      localStorage.setItem('accessToken', accessToken);
      localStorage.setItem('refreshToken', response.data.refreshToken);
      localStorage.setItem('user', JSON.stringify(userData));
      axios.defaults.headers.common['Authorization'] = `Bearer ${accessToken}`;
    } catch (error: any) {
      throw new Error(error.response?.data?.message || 'Login failed');
    }
  };

  useEffect(() => {
    if (!token || !user?.school?.id) {
      return;
    }
    const apiUrl = process.env.REACT_APP_API_URL || 'http://localhost:3000/api';
    const es = new EventSource(`${apiUrl}/updates/school/${user.school.id}?token=${token}`);
    es.onmessage = async () => {
      try {
        await refreshUser();
        window.dispatchEvent(new Event('userRefreshed'));
      } catch {
      }
    };
    return () => {
      es.close();
    };
  }, [token, user?.school?.id, refreshUser]);

  const hasPermission = (permissionCode: string): boolean => {
    if (!user) return false;
    if (user.role.code === 'SUPER_ADMIN') return true;
    return user.permissions?.includes(permissionCode) || false;
  };

  return (
    <AuthContext.Provider
      value={{
        user,
        token,
        login,
        logout,
        refreshUser,
        hasPermission,
        isLoading,
        isAuthenticated: !!user && !!token,
      }}
    >
      {children}
    </AuthContext.Provider>
  );
};

/**
 * Hook to use Auth context
 */
export const useAuth = () => {
  const context = useContext(AuthContext);
  if (context === undefined) {
    throw new Error('useAuth must be used within an AuthProvider');
  }
  return context;
};
